import { AdaptiveSelect } from '../../AdaptiveSelect';
import {
  IDENTITY_FIELDS,
  LEGAL_FORMS,
  type CompanyCardDto,
  type CompanySource,
  type IdentityField,
} from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Button, Card, ErrorText, Field, PageTitle, SectionLoader } from '../../ui';
import { CompanyBrandEditor } from '../SiteBrand';
import { Governance } from './Governance';
import { Conti } from './impresa/Conti';

const LABEL: Record<IdentityField, string> = {
  ragione_sociale: 'Ragione sociale',
  forma_giuridica: 'Forma giuridica',
  codice_fiscale: 'Codice fiscale',
  partita_iva: 'Partita IVA',
  rea: 'REA',
  pec: 'PEC',
  data_costituzione: 'Costituita il',
  capitale_sociale: 'Capitale sociale',
  ateco_primario: 'ATECO prevalente',
  ateco_secondari: 'ATECO secondari',
  oggetto_sociale: 'Oggetto sociale',
  stato_registro: 'Stato nel registro',
  addetti: 'Addetti',
  addetti_al: 'Addetti al',
  fatturato: 'Fatturato (€)',
  totale_bilancio: 'Totale di bilancio (€)',
  anno_bilancio: 'Esercizio del bilancio',
  paese: 'Paese',
};

/** I campi che sono numeri: si scrivono e si salvano come numeri (ADR 0127). */
const NUMERIC = new Set<IdentityField>(['addetti', 'fatturato', 'totale_bilancio', 'anno_bilancio']);
const EUROS = new Set<IdentityField>(['fatturato', 'totale_bilancio']);

const FROM: Record<CompanySource, string> = {
  visura: 'visura',
  persona: 'indicato da te',
  documento: 'documento',
  posta: 'posta',
  sito: 'sito dell’azienda',
  registro: 'pagina pubblica con la P.IVA',
  online: 'pagina online sulla sede',
  supposto: 'supposto dal cervello',
};

/** Pagina Impresa (ADR 0123): l'anagrafica. Organi e cariche e sedi hanno le loro pagine. */
export function CompanyPage() {
  return (
    <>
      <PageTitle title="Impresa" subtitle="Chi è l’impresa: l’anagrafica con la fonte di ogni dato." />
      <CompanyCard />
      <CompanyBrandCard />
    </>
  );
}

/** Il marchio dell'azienda (ADR 0117, 0210 § 3): letto dal sito della società o deciso dal titolare. */
function CompanyBrandCard() {
  const { tenant, isManager } = useTenant();
  return (
    <Card className="mb-4">
      <h2 className="mb-2 text-sm font-semibold">Marchio dell’azienda</h2>
      <CompanyBrandEditor tenantId={tenant.id} manager={isManager} />
    </Card>
  );
}

/** Pagina Organi e cariche (ADR 0123): chi amministra, controlla, rappresenta o possiede l'impresa. */
/** I conti dal bilancio fuori dalla configurazione (ADR 0132): la stessa lettura del passo Visura, per il titolare. */
export function AccountsPage() {
  return (
    <>
      <PageTitle title="Conti" subtitle="Cosa dicono i bilanci della tua azienda: come lavora oggi, cosa è cambiato, cosa non torna tra i documenti e i numeri voce per voce." />
      <Conti />
    </>
  );
}

export function GovernancePage() {
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  return (
    <>
      <PageTitle
        title="Organi e cariche"
        subtitle="Chi amministra, controlla, rappresenta o possiede l’impresa, con il codice carica del Registro delle imprese e la storia di ogni carica."
      />
      {card.data ? <Governance list={card.data.cariche} /> : card.isLoading ? <SectionLoader /> : <ErrorText>{card.error ? errorMessage(card.error) : ''}</ErrorText>}
    </>
  );
}

/**
 * L'impresa (ADR 0021): anagrafica con la fonte di ogni dato e i documenti di partenza che mancano. È il contesto da cui
 * dipendono gli standard di ogni attività.
 */
export function CompanyCard() {
  const { isManager } = useTenant();
  const card = useTenantQuery<CompanyCardDto>(['company'], '/company');
  const [editing, setEditing] = useState(false);
  if (!card.data)
    return card.isLoading ? <p className="py-3 text-sm text-ink/50">Carico l’impresa…</p> : null;
  const { identita, mancanti } = card.data;
  const shown = IDENTITY_FIELDS.filter((f) => {
    const v = identita[f];
    return v !== null && !(Array.isArray(v) && !v.length);
  });
  return (
        <Card className="mb-4">
          <div className="mb-2 flex items-center gap-2">
            <h2 className="flex-1 text-sm font-semibold">L’impresa</h2>
            {isManager && (
              <Button variant="ghost" onClick={() => setEditing(!editing)}>
                {editing ? 'Chiudi' : 'Correggi'}
              </Button>
            )}
          </div>
          {!!mancanti.length && (
            <div className="mb-3 rounded-lg bg-amber-50 p-3 text-sm">
              <p className="mb-1 font-medium">Documenti di partenza che mancano</p>
              <ul className="list-disc pl-5 text-ink/75">
                {mancanti.map((m) => (
                  <li key={m}>{m}</li>
                ))}
              </ul>
              <p className="mt-1 text-xs text-ink/55">
                Caricali nella conversazione della configurazione: il sistema ne ricava i dati con
                le citazioni, senza farti domande.
              </p>
            </div>
          )}
          {!shown.length && <p className="text-sm text-ink/50">Anagrafica non ancora nota.</p>}
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            {shown.map((f) => {
              const v = identita[f];
              const src = identita.fonti[f];
              return (
                <div key={f} className={f === 'oggetto_sociale' ? 'sm:col-span-2' : ''}>
                  <dt className="text-xs text-ink/50">{LABEL[f]}</dt>
                  <dd>
                    {Array.isArray(v) ? v.join('; ') : EUROS.has(f) && typeof v === 'number' ? `${v.toLocaleString('it-IT')} €` : String(v)}
                    {src && (
                      <span className="text-xs text-ink/40" title={src.citazione ?? undefined}>
                        {' '}
                        · {FROM[src.fonte]}
                        {src.rif && src.fonte !== 'persona' ? `: ${src.rif}` : ''}
                      </span>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
          {editing && <IdentityForm card={card.data} onDone={() => setEditing(false)} />}
        </Card>
  );
}

function IdentityForm({ card, onDone }: { card: CompanyCardDto; onDone: () => void }) {
  const { t } = useTenant();
  const qc = useQueryClient();
  const editable = IDENTITY_FIELDS.filter((f) => f !== 'ateco_secondari');
  const [v, setV] = useState<Record<string, string>>(
    Object.fromEntries(
      editable.map((f) => [f, card.identita[f] === null ? '' : String(card.identita[f])]),
    ),
  );
  const save = useMutation({
    mutationFn: () => {
      // Solo i campi cambiati: ciò che scrive la persona vale sopra le letture.
      const body: Record<string, string | number | null> = {};
      for (const f of editable) {
        const before = card.identita[f] === null ? '' : String(card.identita[f]);
        const now = (v[f] ?? '').trim();
        if (now === before) continue;
        body[f] = !now ? null : NUMERIC.has(f) ? Number(now) : now;
      }
      return api(t('/company/identity'), { method: 'PATCH', body });
    },
    onSuccess: () => {
      void qc.invalidateQueries();
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };
  return (
    <form className="mt-4 grid gap-3 border-t border-black/5 pt-4 sm:grid-cols-2" onSubmit={submit}>
      {editable.map((f) =>
        f === 'forma_giuridica' ? (
          <LegalFormSelect
            key={f}
            label={LABEL[f]}
            value={v[f] ?? ''}
            onChange={(x) => setV({ ...v, [f]: x })}
          />
        ) : (
          <Field
            key={f}
            label={LABEL[f]}
            value={v[f] ?? ''}
            type={
              f === 'data_costituzione' || f === 'addetti_al'
                ? 'date'
                : NUMERIC.has(f)
                  ? 'number'
                  : 'text'
            }
            onChange={(e) => setV({ ...v, [f]: e.target.value })}
          />
        ),
      )}
      <div className="flex gap-2 sm:col-span-2">
        <Button type="submit" disabled={save.isPending}>
          Salva
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
      </div>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
    </form>
  );
}

/** La forma giuridica si sceglie tra quelle di legge; un valore scritto prima a parole resta visibile finché non lo cambi. */
export function LegalFormSelect({
  label,
  value,
  onChange,
}: {
  label?: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const known = (LEGAL_FORMS as readonly string[]).includes(value);
  const select = (
    <AdaptiveSelect
      className="w-full rounded-lg border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">{label ? '—' : 'Scegli…'}</option>
      {!known && value && <option value={value}>{value}</option>}
      {LEGAL_FORMS.map((f) => (
        <option key={f} value={f}>
          {f}
        </option>
      ))}
    </AdaptiveSelect>
  );
  if (!label) return select;
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-medium text-ink/80">{label}</span>
      {select}
    </label>
  );
}
